import React from 'react';
import { motion } from 'framer-motion';
import { DatabaseIcon, GlobeIcon, MegaphoneIcon, RefreshCwIcon } from 'lucide-react';
interface SlideProps {
  index: number;
  total: number;
}
const BENEFITS = [{
  icon: RefreshCwIcon,
  title: 'Evergreen & reusable',
  desc: 'One live webinar becomes a permanent, on-demand asset for Medical teams to reuse indefinitely.'
}, {
  icon: DatabaseIcon,
  title: 'Turns replays into data',
  desc: 'Every poll, pause and chapter choice is captured as first-party engagement data.'
}, {
  icon: GlobeIcon,
  title: 'Scales effortlessly',
  desc: 'The same content can be localized and distributed across markets and languages.'
}, {
  icon: MegaphoneIcon,
  title: 'Fuels what comes next',
  desc: 'Engagement signals become the brief for spinoff campaigns and follow-up education.'
}];
const SCOPE = ['DERMX November 2026 webinar — "From complexity to clarity: Understanding, empowering, and optimizing care in Chronic Hand Eczema"', '"Unravelling Chronic Hand Eczema: Subtypes, pathways, and why they matter" — Dr. Raj Chovatiya', '"Living with Chronic Hand Eczema: Practical strategies for everyday management" — Prof. Swen Malte John', '"Best practices in Chronic Hand Eczema: From diagnosis to management" — Prof. Ana María Giménez-Arnau', 'Covers the webinar as delivered live across its 3 repeated sessions (17, 18 & 20 Nov) for global time zones'];
const ASSUMPTIONS = ['Session recordings, slides and speaker consents are final and cleared for reuse', 'No new filming, additional interviews or fresh KOL sessions required', 'Content is built from existing footage — one round of Medical review included', 'Base estimate covers delivery in the webinar\u2019s original language', 'Localization and KOL Teaser Snaks are optional add-ons, priced separately'];
export function Slide02ScopeAssumptions({
  index
}: SlideProps) {
  return <div className="relative w-screen h-screen overflow-hidden bg-[var(--surface)] p-[clamp(2rem,4vw,4rem)] flex flex-col" data-id="element-56" data-mp="1pe4bjefbn0z4">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2,
      duration: 0.6
    }} className="mb-[clamp(1.25rem,2vw,2rem)] flex-shrink-0" data-id="element-57" data-mp="ut8lsa827j7u">
        <p className="text-[var(--brand-green)] text-[clamp(0.875rem,1.5vw,1.125rem)] font-semibold uppercase tracking-wider mb-[clamp(0.5rem,1vw,1rem)]" data-id="element-58" data-mp="14qwdssea6cmb">
          1. Why Snaks
        </p>
        <h2 className="text-[var(--fg)] font-bold text-[clamp(2rem,3.5vw,3rem)] leading-[1.1]" data-id="element-59" data-mp="1djzquzl0kngf">
          Turn one webinar into a{' '}
          <span className="text-[var(--brand-green-light)]" data-id="element-60" data-mp="1sg8o5vlcgqq7">
            reusable growth asset
          </span>
          .
        </h2>
      </motion.div>

      {/* Benefits row */}
      <motion.div initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3,
      duration: 0.5
    }} className="mb-[clamp(1.25rem,2vw,2rem)] grid flex-shrink-0 grid-cols-4 gap-[clamp(0.85rem,1.5vw,1.5rem)]" data-id="element-61" data-mp="3u955u19rerja">
        {BENEFITS.map(benefit => {
        const Icon = benefit.icon;
        return <div key={benefit.title} className="rounded-2xl border-t-8 border-[var(--brand-green)] bg-white p-[clamp(1rem,1.4vw,1.4rem)] shadow-sm" data-id="element-62" data-mp="1v1gc4x8yce0m">
              <span className="mb-3 flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--brand-green)]/10" data-id="element-63" data-mp="1m0i8wh6c8aj3">
                <Icon className="h-[1.125rem] w-[1.125rem] text-[var(--brand-green)]" data-id="element-64" data-mp="1q2lnheoazv8l" />
              </span>
              <h3 className="mb-1.5 text-[clamp(0.95rem,1.1vw,1.1rem)] font-bold text-[var(--fg)]" data-id="element-65" data-mp="1c344xh1lpe7o">
                {benefit.title}
              </h3>
              <p className="text-[clamp(0.8rem,0.95vw,0.92rem)] leading-snug text-[var(--muted)]" data-id="element-66" data-mp="1h3i387deh9si">
                {benefit.desc}
              </p>
            </div>;
      })}
      </motion.div>

      {/* Scope & Assumptions */}
      <div className="flex-1 grid grid-cols-12 gap-[clamp(1.5rem,3vw,3rem)] min-h-0" data-id="element-67" data-mp="1bdp4y0215dlt">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.45,
        duration: 0.6
      }} className="col-span-6 bg-white rounded-2xl p-[clamp(1.5rem,2.2vw,2.2rem)] shadow-sm flex flex-col" data-id="element-68" data-mp="16ec94pt6kw73">
          <h3 className="text-[var(--brand-green)] font-bold text-[clamp(1.05rem,1.4vw,1.25rem)] mb-3.5" data-id="element-69" data-mp="xfqrqxzg3n7w">
            Scope
          </h3>
          <ul className="space-y-3 flex-1" data-id="element-70" data-mp="nncnnykwa4ik">
            {SCOPE.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-71" data-mp="io8e36u264rp" data-mpi={i}>
                <span className="text-[var(--brand-green-light)] text-[clamp(1.1rem,1.3vw,1.3rem)] leading-none mt-0.5" data-id="element-72" data-mp="imf8luv6lqla">
                  •
                </span>
                <span className="text-[var(--fg)] text-[clamp(0.85rem,0.98vw,1rem)] leading-snug" data-id="element-73" data-mp="9ms8dce6uqtt">
                  {item}
                </span>
              </li>)}
          </ul>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.55,
        duration: 0.6
      }} className="col-span-6 bg-[var(--brand-green)] rounded-2xl p-[clamp(1.5rem,2.2vw,2.2rem)] shadow-xl text-white flex flex-col" data-id="element-74" data-mp="2gbkdo3h5csyn">
          <h3 className="text-[var(--brand-green-lighter)] font-bold text-[clamp(1.05rem,1.4vw,1.25rem)] mb-3.5" data-id="element-75" data-mp="2ubboxwga3v8y">
            Assumptions
          </h3>
          <ul className="space-y-3 flex-1" data-id="element-76" data-mp="22k5u9vjbupr2">
            {ASSUMPTIONS.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-77" data-mp="26mj00jy2qnnu" data-mpi={i}>
                <span className="text-[var(--brand-green-lighter)] text-[clamp(1.1rem,1.3vw,1.3rem)] leading-none mt-0.5" data-id="element-78" data-mp="26kziyr4lg8l0">
                  •
                </span>
                <span className="text-white/90 text-[clamp(0.85rem,0.98vw,1rem)] leading-snug" data-id="element-79" data-mp="1eu1szutm3238">
                  {item}
                </span>
              </li>)}
          </ul>
        </motion.div>
      </div>

      {/* Bottom statement */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.8,
      duration: 0.6
    }} className="flex justify-between items-end mt-[clamp(1rem,1.5vw,1.5rem)] flex-shrink-0" data-id="element-80" data-mp="2m59btb2v82fy">
        <p className="text-[var(--fg)] text-[clamp(1rem,1.25vw,1.25rem)] font-semibold" data-id="element-81" data-mp="2qy2gjyyc3as1">
          One trusted source becomes reusable, consistent and locally scalable medical education.
        </p>
      </motion.div>
    </div>;
}